/**
 * LSB Steganography Service
 *
 * Hides encrypted data inside PNG/BMP images using Least Significant Bit technique.
 * The data is embedded in the least significant bits of RGB pixel values,
 * making it imperceptible to the human eye.
 *
 * Security:
 * - Always encrypt data BEFORE hiding (defense in depth)
 * - Use PNG/BMP only (lossless formats)
 * - Avoid JPEG (lossy compression destroys hidden data)
 * - Pattern-based distribution for additional security
 *
 * Limitations:
 * - Fragile to image compression (JPEG)
 * - Fragile to rotation/crop/resize
 * - Detectable by statistical analysis (chi-squared test)
 */

/**
 * Steganography result
 */
export interface StegoResult {
  imageData: ImageData;
  capacity: number;          // Total bytes capacity
  used: number;              // Bytes used
  efficiency: number;        // Percentage used (0-100)
  algorithm: 'LSB-RGB';
  bitsPerPixel: number;
  pattern: 'sequential' | 'random' | 'spread';
}

/**
 * Steganography options
 */
export interface StegoOptions {
  pattern?: 'sequential' | 'random' | 'spread';
  bitsPerPixel?: 1 | 2;     // Bits to use per color channel
  channels?: ('R' | 'G' | 'B')[];
  seed?: string;             // Seed for random pattern (password-based)
}

/**
 * Calculate maximum capacity of an image
 *
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @param bitsPerPixel - Bits to use per pixel (1 or 2)
 * @param channels - Color channels to use (R, G, B)
 * @returns Maximum capacity in bytes
 */
export function calculateCapacity(
  width: number,
  height: number,
  bitsPerPixel: number = 1,
  channels: ('R' | 'G' | 'B')[] = ['R', 'G', 'B']
): number {
  const totalPixels = width * height;
  const channelCount = channels.length;
  const totalBits = totalPixels * channelCount * bitsPerPixel;

  // Reserve 32 bits for data length header
  const usableBits = totalBits - 32;

  return Math.floor(usableBits / 8);
}

/**
 * Simple LCG (Linear Congruential Generator) for pseudo-random pattern
 */
class PRNG {
  private state: number;

  constructor(seed: string) {
    // Hash seed to number
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    this.state = Math.abs(hash) || 1;
  }

  next(): number {
    // LCG parameters (Numerical Recipes)
    const a = 1664525;
    const c = 1013904223;
    const m = Math.pow(2, 32);

    this.state = (a * this.state + c) % m;
    return this.state / m; // Return 0-1
  }
}

/**
 * Encode data into image using LSB steganography
 *
 * @param imageData - Canvas ImageData object
 * @param data - Data to hide (should be encrypted)
 * @param options - Steganography options
 * @returns Modified ImageData with hidden data
 *
 * @throws Error if data is too large for image capacity
 */
export function encodeIntoImage(
  imageData: ImageData,
  data: Uint8Array,
  options: StegoOptions = {}
): StegoResult {
  const {
    pattern = 'sequential',
    bitsPerPixel = 1,
    channels = ['R', 'G', 'B'],
    seed = '',
  } = options;

  const { width, height } = imageData;
  const capacity = calculateCapacity(width, height, bitsPerPixel, channels);

  if (data.length > capacity) {
    throw new Error(
      `Data too large (${data.length} bytes) for image capacity (${capacity} bytes). ` +
      `Use a larger image or reduce data size.`
    );
  }

  // Clone image data
  const result = new ImageData(
    new Uint8ClampedArray(imageData.data),
    width,
    height
  );

  // Channel indices (R=0, G=1, B=2, A=3)
  const channelIndices = channels.map(ch =>
    ch === 'R' ? 0 : ch === 'G' ? 1 : 2
  );

  // Create bit mask for LSB extraction
  const bitMask = bitsPerPixel === 1 ? 0xFE : 0xFC; // ~1 or ~3

  // Prepare data with length header
  const dataLength = data.length;
  const dataWithHeader = new Uint8Array(data.length + 4);

  // Write length as 32-bit big-endian
  dataWithHeader[0] = (dataLength >> 24) & 0xFF;
  dataWithHeader[1] = (dataLength >> 16) & 0xFF;
  dataWithHeader[2] = (dataLength >> 8) & 0xFF;
  dataWithHeader[3] = dataLength & 0xFF;
  dataWithHeader.set(data, 4);

  // Generate pixel sequence based on pattern
  const totalPixels = width * height;
  let pixelSequence: number[];

  if (pattern === 'random' && seed) {
    // Random pattern based on seed
    const prng = new PRNG(seed);
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i);

    // Fisher-Yates shuffle with PRNG
    for (let i = pixelSequence.length - 1; i > 0; i--) {
      const j = Math.floor(prng.next() * (i + 1));
      [pixelSequence[i], pixelSequence[j]] = [pixelSequence[j], pixelSequence[i]];
    }
  } else if (pattern === 'spread') {
    // Spread evenly across image (use capacity for consistent encoding/decoding)
    const step = Math.floor(totalPixels / (capacity * 8 / (channelIndices.length * bitsPerPixel)));
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i * step % totalPixels);
  } else {
    // Sequential (default)
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i);
  }

  // Embed data
  let bitIndex = 0;
  let byteIndex = 0;
  let currentByte = dataWithHeader[0];

  for (const pixelIndex of pixelSequence) {
    if (byteIndex >= dataWithHeader.length) break;

    const pixelOffset = pixelIndex * 4;

    for (const channelOffset of channelIndices) {
      if (byteIndex >= dataWithHeader.length) break;

      const dataIndex = pixelOffset + channelOffset;

      // Clear LSB(s) and set new value
      result.data[dataIndex] = (result.data[dataIndex] & bitMask) |
        ((currentByte >> (8 - bitsPerPixel - bitIndex)) & ((1 << bitsPerPixel) - 1));

      bitIndex += bitsPerPixel;

      if (bitIndex >= 8) {
        bitIndex = 0;
        byteIndex++;
        if (byteIndex < dataWithHeader.length) {
          currentByte = dataWithHeader[byteIndex];
        }
      }
    }
  }

  return {
    imageData: result,
    capacity,
    used: data.length,
    efficiency: (data.length / capacity) * 100,
    algorithm: 'LSB-RGB',
    bitsPerPixel,
    pattern,
  };
}

/**
 * Decode data from image using LSB steganography
 *
 * @param imageData - Canvas ImageData object with hidden data
 * @param options - Steganography options (must match encoding options)
 * @returns Extracted data
 *
 * @throws Error if no data found or data is corrupted
 */
export function decodeFromImage(
  imageData: ImageData,
  options: StegoOptions = {}
): Uint8Array {
  const {
    pattern = 'sequential',
    bitsPerPixel = 1,
    channels = ['R', 'G', 'B'],
    seed = '',
  } = options;

  const { width, height, data } = imageData;

  // Channel indices
  const channelIndices = channels.map(ch =>
    ch === 'R' ? 0 : ch === 'G' ? 1 : 2
  );

  // Generate same pixel sequence as encoding
  const totalPixels = width * height;
  let pixelSequence: number[];

  if (pattern === 'random' && seed) {
    const prng = new PRNG(seed);
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i);

    for (let i = pixelSequence.length - 1; i > 0; i--) {
      const j = Math.floor(prng.next() * (i + 1));
      [pixelSequence[i], pixelSequence[j]] = [pixelSequence[j], pixelSequence[i]];
    }
  } else if (pattern === 'spread') {
    const capacity = calculateCapacity(width, height, bitsPerPixel, channels);
    const step = Math.floor(totalPixels / (capacity * 8 / (channelIndices.length * bitsPerPixel)));
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i * step % totalPixels);
  } else {
    pixelSequence = Array.from({ length: totalPixels }, (_, i) => i);
  }

  const bitMask = (1 << bitsPerPixel) - 1;

  // Extract length header (32 bits)
  const extractedBits = 0;
  let currentByte = 0;
  let bitIndex = 0;
  const headerBytes: number[] = [];

  for (const pixelIndex of pixelSequence) {
    if (headerBytes.length >= 4) break;

    const pixelOffset = pixelIndex * 4;

    for (const channelOffset of channelIndices) {
      if (headerBytes.length >= 4) break;

      const dataIndex = pixelOffset + channelOffset;
      const bits = data[dataIndex] & bitMask;

      currentByte = (currentByte << bitsPerPixel) | bits;
      bitIndex += bitsPerPixel;

      if (bitIndex >= 8) {
        headerBytes.push(currentByte & 0xFF);
        currentByte = 0;
        bitIndex = 0;
      }
    }
  }

  // Parse data length
  const dataLength = (headerBytes[0] << 24) | (headerBytes[1] << 16) |
                     (headerBytes[2] << 8) | headerBytes[3];

  if (dataLength <= 0 || dataLength > calculateCapacity(width, height, bitsPerPixel, channels)) {
    throw new Error('No valid steganography data found in image');
  }

  // Extract actual data
  const result = new Uint8Array(dataLength);
  let resultIndex = 0;
  currentByte = 0;
  bitIndex = 0;
  let channelCount = 0;

  for (const pixelIndex of pixelSequence) {
    if (resultIndex >= dataLength) break;

    const pixelOffset = pixelIndex * 4;

    for (const channelOffset of channelIndices) {
      // Skip exact number of channels used by header (32 bits)
      channelCount++;
      if (channelCount <= 32 / bitsPerPixel) continue;

      if (resultIndex >= dataLength) break;

      const dataIndex = pixelOffset + channelOffset;
      const bits = data[dataIndex] & bitMask;

      currentByte = (currentByte << bitsPerPixel) | bits;
      bitIndex += bitsPerPixel;

      if (bitIndex >= 8) {
        result[resultIndex++] = currentByte & 0xFF;
        currentByte = 0;
        bitIndex = 0;
      }
    }
  }

  return result;
}

/**
 * Detect if image contains steganography data
 *
 * @param imageData - Canvas ImageData object
 * @param options - Steganography options
 * @returns true if steganography signature detected
 */
export function detectSteganography(
  imageData: ImageData,
  options: StegoOptions = {}
): boolean {
  try {
    const data = decodeFromImage(imageData, options);
    return data.length > 0;
  } catch {
    return false;
  }
}

/**
 * Load image file to Canvas ImageData
 *
 * @param file - Image file (PNG/BMP)
 * @returns Promise resolving to ImageData
 */
export async function loadImageFile(file: File): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      resolve(imageData);
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert ImageData to downloadable PNG Blob
 *
 * @param imageData - Canvas ImageData object
 * @returns Promise resolving to PNG Blob
 */
export async function imageToPNGBlob(imageData: ImageData): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    ctx.putImageData(imageData, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to create PNG blob'));
      }
    }, 'image/png');
  });
}

/**
 * Validate image format (must be PNG or BMP)
 *
 * @param file - Image file
 * @returns true if format is valid
 */
export function validateImageFormat(file: File): boolean {
  const validTypes = ['image/png', 'image/bmp', 'image/x-bmp'];
  return validTypes.includes(file.type);
}

/**
 * Get image format error message
 *
 * @param file - Image file
 * @returns Error message or null if valid
 */
export function getFormatError(file: File): string | null {
  if (!validateImageFormat(file)) {
    return `Invalid format: ${file.type}. Please use PNG or BMP only. JPEG is not supported (lossy compression).`;
  }
  return null;
}
